<template>
  <teleport v-if="hasMounted" to="#portal-target">
    <!-- A modal is an overlay over the page, not a click elsewhere in it: dismissing one — by the
         backdrop, the close button or Cancel — must leave a table's cell selection alone, or the
         rows a dialog was opened about are gone by the time it closes. Marked on the shell rather
         than per modal, since a teleport root cannot carry a fallthrough attribute. -->
    <transition name="simple-fade-in-out">
      <div v-if="isVisible" class="backdrop" data-keeps-cell-selection @mousedown="onOutsideClick"></div>
    </transition>
    <transition name="fall-from-top-with-fade" appear>
      <div
        v-if="isOpen || keepMounted"
        ref="modal-wrap"
        :class="{
          'modal-wrap': true,
          'modal-wrap--error': isError,
          'modal-wrap--do-not-render': !isOpen,
          'modal-wrap--invisible': !isVisible,
        }"
        data-keeps-cell-selection
      >
        <div class="modal-wrap__in-wrap" @mousedown="onOutsideClick">
          <div
            :class="{
              modal: true,
              'modal--invisible': !isVisible,
            }"
            @mousedown.stop="() => {}"
          >
            <fs-button-icon
              v-if="!hideCloseButton"
              :icon="cancelIcon"
              class="modal__close-button"
              width="3.2rem"
              height="3.2rem"
              color="dark-blue"
              @click="close"
            />

            <!-- The forms these modals host say "I am done" the way they have always said it, by
                 dispatching `popper-close` — they were written for an anchored popper, which
                 listens for exactly this. Without it a form's Cancel reverted its values and left
                 the modal standing. `.stop`, as fs-popper does it: the innermost overlay owns the
                 event, so a popper inside the modal closes itself and not the modal around it. -->
            <div class="modal__content" @popper-close.stop="close">
              <slot />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import FsButtonIcon from '@components/fs-button-icon.vue';
import cancelIcon from '@assets/icons/other/cancel.svg';
import { useMounted } from '@lib/use-mounted.ts';
import { useShortcutLayer } from '@lib/shortcuts.ts';
import { waitTimeout } from '@lib/promise.ts';
import { lockWindowScroll, unlockWindowScroll } from '@lib/window-scrollbar.ts';
import { onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';

const emits = defineEmits<{ close: []; open: [] }>();

const { isError, keepMounted, disableCloseOnOutsideClick, hideCloseButton, disableScrollingLogic } = defineProps<{
  isError?: boolean; // If true, modal will get higher prio and show on top of everything else
  disableCloseOnOutsideClick?: boolean;
  hideCloseButton?: boolean;
  disableScrollingLogic?: boolean; // This is an ugly hack. Modals in modals scroll the screen in weird ways otherwise.
  keepMounted?: boolean; // Keep components inside modal mounted at all time, even when not visible
}>();

const hasMounted = useMounted();
const isOpen = ref(false);
const isVisible = ref(false); // needed to fix the bug with not working transition on the popper window

const modalWrap = useTemplateRef('modal-wrap');

const { register } = useShortcutLayer(isOpen, 'Dialog');
register('Escape', close, { description: 'Close dialog' });

let isScrollLocked = false;

function lockScroll() {
  if (disableScrollingLogic) return;

  lockWindowScroll();
  isScrollLocked = true;
}

function unlockScroll() {
  if (!isScrollLocked) return;
  isScrollLocked = false;

  unlockWindowScroll();
}

async function close() {
  if (!isOpen.value) return;

  isVisible.value = false;

  await waitTimeout(120);

  isOpen.value = false;
  unlockScroll();

  emits('close');
}

async function open() {
  isOpen.value = true;
  lockScroll();

  emits('open');

  await waitTimeout(0);
  isVisible.value = true;
}

// An action taken inside the modal can unmount its owner (e.g. deleting the item
// the modal belongs to), which skips close() entirely and would leave the body
// scroll-locked for good.
onBeforeUnmount(unlockScroll);

async function onOutsideClick() {
  if (disableCloseOnOutsideClick) return;

  await close();
}

defineExpose({
  open,
  close,
  toggle: async () => (isOpen.value ? await close() : await open()),

  isElementInside: (element: HTMLElement) => {
    return modalWrap.value?.contains(element);
  },
});
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/mixins';

// Modal styles ===============================================================
.modal {
  height: auto;
  width: auto;
  max-width: calc(100vw - 6rem);
  border-radius: $br-card;
  transform: translateY(0);
  background: $c-surface;
  box-shadow: $shadow-lifted;
  transition: transform 0.12s ease-in-out;

  &--invisible {
    transform: translateY(5rem);
  }

  &__close-button {
    position: absolute;
    background-color: $c-bg-light;
    border-radius: 50%;
    margin: 0;
    right: -1rem;
    top: -1rem;
    z-index: 1;
    cursor: pointer;
  }

  &__content {
    overflow: hidden;
    border-radius: inherit;
  }
}

.backdrop {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: $zi-modal;
  background: rgba($c-ink, 0.16);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.modal-wrap {
  @include scrollbarStyling;
  width: calc(100vw - 2.7rem);
  height: 100vh;
  max-height: 100vh;
  box-sizing: border-box;
  position: fixed;
  inset: 0 auto auto 0;
  z-index: $zi-modal;
  overflow-y: auto;
  overscroll-behavior: contain;
  opacity: 1;
  transition: opacity 0.12s ease-in-out;

  &--error {
    z-index: $zi-error-modal;
  }

  &--invisible {
    opacity: 0;
  }

  &--do-not-render {
    display: none;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.8);
  }

  &:hover::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 1);
  }

  &__in-wrap {
    width: 100%;
    min-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    align-content: center;
  }
}
</style>
